import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  FormSchema,
  FormValues,
  defaultSources
} from '../schema';
import {IIngestionCopyResponse} from '../../../../api/types/ingestionApi.type';
import {useIngestionDropdowns} from './useIngestionDropdowns';
import {useIngestionFormEffects} from './useIngestionFormEffects';

export const useIngestionForm = (
  loadedData: IIngestionCopyResponse | null,
  dataLoaded: boolean,
  isCopyFlow: boolean,
  isEditFlow: boolean,
  projectId: number
) => {
  // State for tracking prefill progress
  const [folderAccessError, setFolderAccessError] = useState(false);
  const [editDataPrefilled, setEditDataPrefilled] = useState(false);
  const [folderPrefilled, setFolderPrefilled] = useState(false);
  const [sourcesPrefilled, setSourcesPrefilled] = useState(false);
  const [lastProcessedFolder, setLastProcessedFolder] = useState<string | null>(
    null
  );

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      ingestionType: null,
      sourceSystem: null,
      serverLocation: null,
      sourceFolder: null,
      fieldSeparator: null,
      textQualifier: null,
      multiValue: null,
      dateFormat: null,
      timeZone: null,
      sources: defaultSources,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {watch, setValue, getValues, clearErrors} = form;

  // Watch values
  const selectedServerLocation = watch('serverLocation');
  const selectedFolder = watch('sourceFolder');
  const fieldSeparatorValue = watch('fieldSeparator');
  const textQualifierValue = watch('textQualifier');
  const dataDelimiterValue = watch('multiValue');
  const datLoadFile = watch('sources.dat.loadFile');

  // Get all dropdown data
  const dropdownData = useIngestionDropdowns(
    selectedServerLocation,
    selectedFolder,
    fieldSeparatorValue,
    textQualifierValue,
    dataDelimiterValue,
    datLoadFile,
    projectId
  );

  // Apply all effects
  useIngestionFormEffects({
    setValue,
    getValues,
    clearErrors,
    selectedServerLocation,
    selectedFolder,
    fieldSeparatorValue,
    textQualifierValue,
    dataDelimiterValue,
    datLoadFile,
    columnDelimeter: dropdownData.columnDelimeter,
    dataDelimeter: dropdownData.dataDelimeter,
    textQualifier: dropdownData.textQualifier,
    timeZone: dropdownData.timeZone,
    dateFormat: dropdownData.dateFormat,
    sourceSystem: dropdownData.sourceSystem,
    serverLocation: dropdownData.serverLocation,
    sourceFolderOptions: dropdownData.sourceFolderOptions,
    loadFiles: dropdownData.loadFiles,
    headersDocData: dropdownData.headersDocData,
    rawSourceFolders: dropdownData.rawSourceFolders,
    wizardData: dropdownData.wizardData,
    isLoading: dropdownData.isLoading,
    datFileValue: dropdownData.datFileValue,
    linkType: dropdownData.linkType,
    annotationLayerOptions: dropdownData.annotationLayerOptions,
    isCopyFlow,
    isEditFlow,
    loadedData,
    dataLoaded,
    setFolderAccessError,
    editDataPrefilled,
    setEditDataPrefilled,
    folderPrefilled,
    setFolderPrefilled,
    sourcesPrefilled,
    setSourcesPrefilled,
    lastProcessedFolder,
    setLastProcessedFolder,
  });

  return {
    form,
    wizardData: dropdownData.wizardData,
    sourceHeadersData: dropdownData.sourceHeadersData,
    folderAccessError,
    setFolderAccessError,
    dropdownData: {
      lstServerLocation: dropdownData.lstServerLocation,
      sourceSystem: dropdownData.sourceSystem,
      serverLocation: dropdownData.serverLocation,
      columnDelimeter: dropdownData.columnDelimeter,
      dataDelimeter: dropdownData.dataDelimeter,
      textQualifier: dropdownData.textQualifier,
      timeZone: dropdownData.timeZone,
      dateFormat: dropdownData.dateFormat,
      linkType: dropdownData.linkType,
      sourceFolderOptions: dropdownData.sourceFolderOptions,
      loadFiles: dropdownData.loadFiles,
      headersDocData: dropdownData.headersDocData,
      ingestionTypeOptions: dropdownData.ingestionTypeOptions,
      annotationLayerOptions: dropdownData.annotationLayerOptions,
    },
  };
};
